import { useUpdateConversationMessageRatingExplanationMutation } from "#features/conversations/api/useUpdateConversationMessageRatingExplanationMutation.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import type { ConversationMessage } from "#types/types.ts";
import { Button, ButtonGroup, Content, Form, Heading, Modal, Paragraph, Stack, TextArea } from "@hopper-ui/components";
import { type SyntheticEvent, useCallback, useState } from "react";
import { toast } from "react-toastify";

interface Props {
    conversationId: string;
    message: ConversationMessage;
    isOpen: boolean;
    closeRatingModal: () => void;
}

const formId = "ratingForm";

export const RatingModal = ({ conversationId, message: { id, ratingExplanation }, isOpen, closeRatingModal }: Props) => {
    const { tShared } = useSharedTranslation();
    const [explanation, setExplanation] = useState(ratingExplanation ?? "");
    const { trackEvent } = useTracking();
    const { mutate, isPending } = useUpdateConversationMessageRatingExplanationMutation();

    const onSuccess = useCallback(() => {
        toast.success(tShared("rating.success"));
        closeRatingModal();
    }, [closeRatingModal, tShared]);

    const onError = useCallback(() => {
        toast.error(tShared("rating.error"));
    }, [tShared]);

    const handleSubmit = useCallback((event: SyntheticEvent) => {
        event.preventDefault();
        trackEvent(MixpanelEvent.ButtonClicked, { Location: "Rating Modal", Trigger: "Submit" });
        mutate({
            conversationId,
            conversationMessageId: id,
            explanation
        }, { onSuccess, onError });
    }, [conversationId, explanation, id, mutate, onError, onSuccess, trackEvent]);

    const handleOpenChange = useCallback((_isOpen: boolean) => {
        if (!_isOpen) {
            closeRatingModal();
        }
    }, [closeRatingModal]);

    return (
        <Modal isOpen={isOpen} onOpenChange={handleOpenChange} size="sm">
            {({ close }) => (
                <>
                    <Heading>{tShared("rating.title")}</Heading>
                    <Content>
                        <Form id={formId} onSubmit={handleSubmit}>
                            <Stack gap="inline-lg">
                                <Paragraph size="sm">
                                    {tShared("rating.description")}
                                </Paragraph>
                                <TextArea
                                    aria-label={tShared("rating.description")}
                                    placeholder={tShared("rating.placeholder")}
                                    rows={4}
                                    isFluid
                                    value={explanation}
                                    onChange={value => setExplanation(value)}
                                />
                            </Stack>
                        </Form>
                    </Content>
                    <ButtonGroup>
                        <Button variant="secondary" onPress={close}>{tShared("rating.skip")}</Button>
                        <Button form={formId} type="submit" isLoading={isPending}>{tShared("rating.send")}</Button>
                    </ButtonGroup>
                </>
            )}
        </Modal>
    );
};
